import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Loader2, CheckCircle2, FileText, Briefcase, Lightbulb, GraduationCap } from "lucide-react";
import { debugLog } from "../lib/logger";
import { T } from "@/locales/tr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { id: "agence_comm", label: "Agence de communication", desc: "Identité visuelle, photo, web, copywriting, maquette", Icon: Briefcase },
  { id: "conseil_projet", label: "Conseil & développement de projet", desc: "Business plan, marché, MVP, investisseurs", Icon: Lightbulb },
  { id: "formation", label: "Formation & assistance", desc: "Photo, direction artistique, web design, IA", Icon: GraduationCap },
  { id: "autre", label: "Autre besoin sur-mesure", desc: "Décris ton projet, Papicoo s'adapte", Icon: FileText },
];

export default function DevisExpress() {
  const [params] = useSearchParams();
  const [service, setService] = useState("agence_comm");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [brief, setBrief] = useState("");
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const id = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/devis-express/status/${sid}`);
        if (data.ready) {
          clearInterval(id);
          setPolling(false); setDone(true);
          toast.success(T("Devis envoyé par email 🌹"));
        }
      } catch (e) { debugLog("status:", e?.message); }
      if (attempts > 24) { clearInterval(id); setPolling(false); }
    }, 2500);
    return () => clearInterval(id);
  }, [params]);

  const submit = async () => {
    if (!name.trim()) { toast.error(T("Ton prénom requis.")); return; }
    if (!email.trim()) { toast.error("Email requis."); return; }
    if (brief.length < 30) { toast.error(T("Décris ton projet en au moins 30 caractères.")); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/devis-express/checkout`, {
        service, name, email, brief, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("Échec checkout."));
      setLoading(false);
    }
  };

  if (done) return (
    <div data-testid="page-devis-express" className="max-w-2xl mx-auto px-4 sm:px-8 py-16 text-center">
      <CheckCircle2 size={64} className="mx-auto text-emerald-600 mb-4" />
      <h1 className="font-display text-4xl text-[color:var(--papicoo-ink)] mb-3">{T("Devis envoyé 🌹")}</h1>
      <p className="text-[color:var(--papicoo-muted)]">{T("Vérifie ta boîte mail — ton devis personnalisé t'attend, signé Papicoo. Réponse directe à")} <strong>{T("papicoo@papicoo.fr")}</strong> {T("pour valider.")}</p>
    </div>
  );

  if (polling) return (
    <div data-testid="page-devis-express" className="max-w-2xl mx-auto px-4 sm:px-8 py-16 text-center">
      <Loader2 className="animate-spin mx-auto mb-4" size={48} />
      <h2 className="font-display text-2xl text-[color:var(--papicoo-ink)]">{T("Génération du devis par Papicoo IA…")}</h2>
      <p className="text-[color:var(--papicoo-muted)] mt-2 text-sm">{T("≈ 30 secondes. Ne ferme pas cette page.")}</p>
    </div>
  );

  return (
    <div data-testid="page-devis-express" className="max-w-3xl mx-auto px-4 sm:px-8 py-10">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)] inline-flex items-center gap-2">
        <FileText size={14} className="text-[color:var(--papicoo-gold)]" /> {T("PAPICOO · DEVIS EXPRESS IA")}
      </div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2 leading-[0.98]">
        {T("Un devis pro.")}<br />
        <span className="italic font-light">{T("Dans ta boîte. En 30 secondes.")}</span>
      </h1>
      <p className="text-[color:var(--papicoo-muted)] mt-3 max-w-xl">
        {T("Décris ton projet, paie 19€, et reçois immédiatement un devis personnalisé signé Papicoo avec livrables, calendrier, tarif et conditions. Pas de rendez-vous, pas d'attente.")}
      </p>

      <div className="mt-8 grid sm:grid-cols-2 gap-3">
        {SERVICES.map((s) => (
          <button
            key={s.id}
            data-testid={`devis-svc-${s.id}`}
            onClick={() => setService(s.id)}
            className={`papicoo-card p-4 text-left transition ${service === s.id ? "ring-2 ring-[color:var(--papicoo-gold)] bg-white" : "hover:shadow-md bg-white/70"}`}
          >
            <s.Icon size={20} className="text-[color:var(--papicoo-gold)] mb-2" />
            <div className="font-display text-lg text-[color:var(--papicoo-ink)]">{T(s.label)}</div>
            <div className="text-xs text-[color:var(--papicoo-muted)] mt-1">{T(s.desc)}</div>
          </button>
        ))}
      </div>

      <div className="mt-6 papicoo-card p-5 space-y-3">
        <div className="grid sm:grid-cols-2 gap-2">
          <input data-testid="devis-name" placeholder={T("Ton prénom")} value={name} onChange={(e) => setName(e.target.value)} className="px-3 py-2 rounded-lg border border-[color:var(--papicoo-border)] text-sm" />
          <input data-testid="devis-email" type="email" placeholder={T("Ton email")} value={email} onChange={(e) => setEmail(e.target.value)} className="px-3 py-2 rounded-lg border border-[color:var(--papicoo-border)] text-sm" />
        </div>
        <textarea
          data-testid="devis-brief"
          rows={7}
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          placeholder={T("Décris ton projet : objectif, contexte, deadline, budget approximatif si tu en as un. Plus tu es précis, plus le devis sera juste.")}
          className="w-full px-3 py-2 rounded-lg border border-[color:var(--papicoo-border)] text-sm leading-relaxed"
        />
        <div className="text-[11px] text-[color:var(--papicoo-muted)] text-right">{brief.length} caractères (min 30)</div>
        <button
          data-testid="devis-submit"
          onClick={submit}
          disabled={loading || brief.length < 30}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full papicoo-btn-gold text-sm disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          {loading ? "Redirection Stripe…" : T("Payer 19€ et recevoir mon devis")}
        </button>
        <p className="text-[10px] italic text-[color:var(--papicoo-muted)] text-center">
          {T("Paiement sécurisé Stripe. Le devis est généré par Papicoo IA et envoyé à ton email après paiement (≈ 30s).")}
          <br />{T("Si tu valides, tu paies l'acompte 30% pour démarrer.")}
        </p>
      </div>
    </div>
  );
}
